import { useState, useEffect } from 'react';
import { Users, CalendarDays, ChevronRight, Search } from '../theme/icons.js';
import { API } from '../App.js';
import { useEvent } from '../contexts/EventContext.js';
import { useRole } from '../hooks/useRole.js';
import { Wegzeichen } from '../theme/Wegzeichen.js';
import { SkeletonTable } from '../components/ui/Skeleton.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { Select } from '../components/ui/Select.js';
import { ChipRow } from '../components/ui/ChipRow.js';
import { MONATE, alsDatum, beginn, ende, istArchiviert, neuesteZuerst, zeitraum }
  from '../features/events/datum.js';
import { passt } from '../features/suche.js';
import './Dashboard.css'; // .toolbar, .search-wrap, .stat-chip
import './Start.css';

/* Startseite — eine Zeile pro Veranstaltung, für die du angemeldet bist oder
   auf die du Zugriff hast.

   Aufbau ist das .event-row-Muster der Website (Termine-Seite): links der
   Datumsblock, in der Mitte Name und Fakten, rechts der Status. Der Status
   steht dort absichtlich als Schrift, nicht als Chip — „hebt sich klar von
   Buttons/Chips ab" (vendor/dpsg/css/style.css). */

/* Sortierungen der Werkzeugleiste. Die erste ist die Vorgabe und die
   Ordnung, die die Seite schon immer hatte. */
const SORTIERUNGEN = [
  { value: 'neu', label: 'Neueste zuerst', vergleich: (a, b) => neuesteZuerst(a.event, b.event) },
  { value: 'alt', label: 'Älteste zuerst', vergleich: (a, b) => beginn(a.event) - beginn(b.event) },
  {
    value: 'name',
    label: 'Name A–Z',
    vergleich: (a, b) => eventName(a.event).localeCompare(eventName(b.event), 'de'),
  },
  {
    value: 'anmeldungen',
    label: 'Meiste Anmeldungen',
    vergleich: (a, b) => b.count - a.count || neuesteZuerst(a.event, b.event),
  },
];

/* Filter-Chips. „Nur Zugriff" heißt: Du siehst die Veranstaltung, bist
   aber selbst nicht angemeldet — der Fall der Leitungen und Admins. */
const FILTER = [
  { value: 'alle', label: 'Alle', passt: () => true },
  { value: 'angemeldet', label: 'Angemeldet', passt: (r) => r.myCount > 0 },
  { value: 'zugriff', label: 'Nur Zugriff', passt: (r) => r.myCount === 0 },
];

const eventName = (ev) => ev.name?.de || ev.slug;

export default function Start({ onNavigate }) {
  const { events, setSelectedEvent } = useEvent();
  const { role } = useRole();
  const [rows, setRows] = useState(null);
  /* null = Profil noch nicht beantwortet, '' = beantwortet, aber ohne Namen.
       Die Unterscheidung ist der ganze Punkt: vorher stand hier '' für beides,
       und weil '' als "kein Name" gilt, blitzte beim Laden "Übersicht" auf und
       wurde eine Zehntelsekunde später zu "Hallo Jan". */
  const [firstName, setFirstName] = useState(null);
  const [archivOffen, setArchivOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const [filter, setFilter] = useState('alle');
  const [sortierung, setSortierung] = useState('neu');

  useEffect(() => {
    let alive = true;
    const laden = API.getCallerProfile?.();
    // Kein solcher Aufruf im Handler: dann gibt es auch nie einen Namen.
    if (!laden) {
      setFirstName('');
      return;
    }
    laden
      .then((p) => {
        if (alive) setFirstName(p?.name || '');
      })
      .catch(() => {
        if (alive) setFirstName('');
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!events?.length) {
      setRows([]);
      return undefined;
    }
    let alive = true;
    (async () => {
      // Alle Veranstaltungen parallel — sequenziell würde die Seite bei
      // mehreren Lagern sichtbar langsam aufbauen.
      const result = await Promise.all(
        events.map(async (ev) => {
          /* Lightweight counts-only endpoint — kein Ticket-Loading nötig. */
         const counts = await API.getTicketCounts(ev.organizer, ev.slug).catch(() => ({
            hasAccess: false,
            total: 0,
            myCount: 0,
          }));
          return { event: ev, hasAccess: !!counts.hasAccess, count: counts.total || 0, myCount: counts.myCount || 0 };
        })
      );
      if (!alive) return;
      /* Chronologisch, neueste oben — in der Hauptliste wie im Archiv.
         Vorher standen kommende Veranstaltungen aufsteigend vorn und
         vergangene absteigend dahinter, also zwei Ordnungen in einer Liste.
         Eine Ordnung ist vorhersagbarer. */
      result.sort((a, b) => neuesteZuerst(a.event, b.event));
       // filter() liefert ein neues Array — das Ergebnis muss gespeichert werden,
       // sonst bleibt jede Veranstaltung sichtbar.
     setRows(result.filter(r => r.hasAccess && (r.count > 0 || r.myCount > 0))); // Nur mit Zugriff (hasAccess) und mindestens einer Anmeldung (total > 0) oder eigenen Anmeldung (myCount > 0) anzeigen.
    })();
    return () => {
      alive = false;
    };
  }, [events]);

  function open(ev) {
    setSelectedEvent(ev);
    onNavigate('dashboard');
  }

  /* Solange das Profil unterwegs ist, bleibt die Zeile leer statt falsch.
       Das geschützte Leerzeichen hält die Höhe, damit darunter nichts springt.

       Der Name ist der Akzent im Titel und steht deshalb in <em>. Die Website
       zeichnet Akzentwörter in Überschriften genau so aus: `.sec-title em` ist
       dort DPSG-Blau und kursiv (vendor/dpsg/css/style.css). Ausgezeichnet wird
       nur der Name, nicht das "Hallo" — sonst ist es kein Akzent mehr. */
  const greeting =
    firstName === null ? (
      ' '
    ) : firstName ? (
      <>
        Hallo <em>{firstName}</em>
      </>
    ) : (
      'Übersicht'
    );

  /* Einmal pro Aufbau bestimmt, nicht pro Zeile: sonst könnte eine Zeile
     gegen einen anderen Zeitpunkt geprüft werden als die nächste. */
  const jetzt = Date.now();

  /* Suche, Filter und Sortierung gelten für beide Listen gleich. Die Suche
     übersieht Akzente („diozesan" findet „Diözesanlager"). */
  const filterRegel = FILTER.find((f) => f.value === filter) || FILTER[0];
  const vergleich = (SORTIERUNGEN.find((o) => o.value === sortierung) || SORTIERUNGEN[0]).vergleich;
  const gefiltert = (rows || [])
    .filter(filterRegel.passt)
    .filter((r) => passt(`${eventName(r.event)} ${r.event.slug}`, suche))
    .sort(vergleich);
  const aktuelle = gefiltert.filter(r => !istArchiviert(r.event, jetzt));
  const archiv   = gefiltert.filter(r =>  istArchiviert(r.event, jetzt));
  const eingeschraenkt = filter !== 'alle' || suche.trim() !== '';

  /* Trifft die Suche nur Archiviertes, klappt das Archiv auf — sonst stünde
     unter der Leiste nur „Nichts Aktuelles" und der Treffer bliebe
     versteckt. Zugeklappt wird nicht automatisch: Wer es offen hat, will
     es offen. */
  const nurImArchiv = eingeschraenkt && aktuelle.length === 0 && archiv.length > 0;
  useEffect(() => {
    if (nurImArchiv) setArchivOffen(true);
  }, [nurImArchiv]);

  /* Zähler an den Chips: wie viele Veranstaltungen der Filter zeigen
     würde, unabhängig von der Suche — wie die Stufen-Chips der Anmeldungen.
     Die Reihe erscheint nur, wenn es beides gibt: Bist du überall
     angemeldet (oder nirgends), stünde neben „Alle 3" nur „Angemeldet 3",
     und der Chip filterte nichts. */
  const chips = FILTER.map((f) => ({ ...f, anzahl: (rows || []).filter(f.passt).length }));
  const zeigeChips = chips.every((f) => f.anzahl > 0);

  /* Eine Zeile, zwei Listen — deshalb hier und nicht zweimal im JSX. */
  function zeile({ event: ev, count, myCount }) {
    const start = alsDatum(ev.date_from || ev.date_to);
    const e = ende(ev);
    /* Ohne Datum ist eine Veranstaltung nicht »vorbei« — sonst stünde
       der Chip neben keinem Datum. */
    const vorbei = e !== null && e < jetzt;
    // Superadmin sieht das ganze Event (count = total). Eingeschränkte Rollen
    // sehen ihre sichtbare Menge (count = der total, den die Backend-Abfrage
    // zurückgibt) — kleiner als das ganze Event, deshalb "für dich sichtbar"
    // (Issue #60).
    const seesSubset = role !== 'superadmin';
    return (
      <button
        key={`${ev.organizer}/${ev.slug}`}
        className="event-row"
        onClick={() => open(ev)}
        aria-label={`${ev.name?.de || ev.slug} öffnen`}
      >
        {/* Reihenfolge und Größen wie auf der Website: Tag groß, Monat klein
            darunter. Das Jahr steht in der Meta-Zeile daneben. */}
        <span className="ev-date">
          <span className="ev-day">{start ? start.getDate() : '–'}</span>
          <span className="ev-month">{start ? MONATE[start.getMonth()] : '—'}</span>
        </span>

        <span className="ev-body">
          <span className="ev-name">{ev.name?.de || ev.slug}</span>
          <span className="ev-meta">
            <span><CalendarDays size={15} aria-hidden="true" />{zeitraum(ev.date_from, ev.date_to)}</span>
             <span><Users size={15} aria-hidden="true" />
                {seesSubset
                ? (count === 1 ? '1 Anmeldung für dich sichtbar' : `${count} Anmeldungen für dich sichtbar`)
                : count === 1 ? '1 Anmeldung' : `${count} Anmeldungen`}
             </span>
          </span>
        </span>

        <span className="ev-right">
          {/* Die Farbe reist als --cc mit, wie bei den Stufen-Chips:
              chalk.css macht daraus Umriss und Schrift. */}
          {myCount > 0 && (
            <span className="ev-status" style={{ '--cc': 'var(--ak-pfadi)' }}>
              Angemeldet
            </span>
          )}
          {vorbei && (
            <span className="ev-status" style={{ '--cc': 'var(--text-muted)' }}>
              Vorbei
            </span>
          )}
          <ChevronRight size={18} className="ev-chevron" aria-hidden="true" />
        </span>
      </button>
    );
  }

  return (
    <div className="start-page">
      <div className="page-header">
        <h1 className="page-title">{greeting}</h1>
        <p className="page-subtitle">
          Die Veranstaltungen, für die du angemeldet bist oder Zugriff hast.
        </p>
      </div>

      {rows === null && <SkeletonTable />}

      {rows !== null && rows.length === 0 && (
        <EmptyState icon={<Wegzeichen sign="lager" />} title="Noch keine Veranstaltung">
          Sobald du angemeldet bist oder Zugriff bekommst, steht sie hier.
        </EmptyState>
      )}

      {rows !== null && rows.length > 0 && (
        <>
          {zeigeChips && (
            <div className="stat-chips" role="group" aria-label="Veranstaltungen filtern">
              {/* Eine Zeile, sonst „Mehr" — wie in den Anmeldungen (§6). */}
              <ChipRow ariaLabel="Weitere Filter">
              {chips.map((f) => (
                <button
                  key={f.value}
                  className={`stat-chip ${filter === f.value ? 'active' : ''}`}
                  aria-pressed={filter === f.value}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                  <span className="stat-chip-count">{f.anzahl}</span>
                </button>
              ))}
              </ChipRow>
            </div>
          )}

          {/* Immer sichtbar, auch bei drei Veranstaltungen: dann weiß man,
              wo sie sitzt, wenn es mehr werden. Eine Zeile (DESIGN-SYSTEM.md
              §6): Suche nimmt den Platz, die Sortierung so breit wie nötig. */}
          <div className="toolbar start-toolbar">
            <div className="search-wrap">
              <span className="search-icon-left">
                <Search size={16} />
              </span>
              <input
                type="search"
                aria-label="Veranstaltungen durchsuchen"
                className="search-input"
                placeholder="Veranstaltung suchen…"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
              />
            </div>
            <Select
              className="start-sortierung"
              ariaLabel="Sortierung"
              value={sortierung}
              onChange={setSortierung}
              options={SORTIERUNGEN.map(({ value, label }) => ({ value, label }))}
            />
          </div>

          {gefiltert.length === 0 && (
            <EmptyState compact icon={<Search size={28} />} title="Keine Treffer">
              Keine Veranstaltung passt zu Suche oder Filter.
            </EmptyState>
          )}

          {aktuelle.length > 0 && (
            <div className="event-list">{aktuelle.map(zeile)}</div>
          )}

          {/* Kann vorkommen: alles archiviert, Hauptliste leer. */}
          {aktuelle.length === 0 && archiv.length > 0 && (
            <p className="ev-alles-archiviert">
              {eingeschraenkt
                ? 'Treffer nur im Archiv.'
                : 'Nichts Aktuelles. Ältere Veranstaltungen liegen im Archiv.'}
            </p>
          )}

          {/* Alles, was länger als zwei Wochen vorbei ist, liegt zugeklappt
              darunter: aus dem Blickfeld, aber einen Klick entfernt. */}
          {archiv.length > 0 && (
            <div className="ev-archiv">
              <button
                className="ev-archiv-kopf"
                onClick={() => setArchivOffen(o => !o)}
                aria-expanded={archivOffen}
                aria-controls="ev-archiv-liste"
              >
                <ChevronRight
                  size={16}
                  className={`ev-archiv-pfeil ${archivOffen ? 'offen' : ''}`}
                  aria-hidden="true"
                />
                Archiv
                <span className="ev-archiv-zahl">{archiv.length}</span>
              </button>
              <div className="event-list" id="ev-archiv-liste" hidden={!archivOffen}>
                {archiv.map(zeile)}
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );
}
